/**
 * Shared concurrency pool for /reports/run.
 * Module-level state — one pool for every WidgetRenderer in the app.
 * Caps in-flight widget fetches so dense tabs do not stampede the API.
 */

const MAX_CONCURRENT = 4;

let activeCount = 0;
/** @type {Job[]} */
const waiting = [];

/**
 * @typedef {object} Job
 * @property {AbortSignal} [signal]
 * @property {() => Promise<unknown>} start
 * @property {(value: unknown) => void} resolve
 * @property {(reason?: unknown) => void} reject
 * @property {boolean} settled
 * @property {() => void} [onAbort]
 */

function canceledError() {
  const err = new Error('canceled');
  err.name = 'CanceledError';
  err.code = 'ERR_CANCELED';
  return err;
}

function settle(job, fn, value) {
  if (job.settled) return;
  job.settled = true;
  if (job.signal && job.onAbort) {
    job.signal.removeEventListener('abort', job.onAbort);
  }
  fn(value);
}

function pump() {
  while (activeCount < MAX_CONCURRENT && waiting.length > 0) {
    const job = waiting.shift();
    if (job.signal?.aborted) {
      settle(job, job.reject, canceledError());
      continue;
    }

    activeCount += 1;
    Promise.resolve()
      .then(() => {
        // Aborted after leaving the queue but before the request starts.
        if (job.signal?.aborted) {
          throw canceledError();
        }
        return job.start();
      })
      .then(
        (value) => settle(job, job.resolve, value),
        (err) => settle(job, job.reject, err),
      )
      .finally(() => {
        activeCount -= 1;
        pump();
      });
  }
}

/**
 * Run `start` when a pool slot is free.
 * If `signal` aborts while still queued, the job is dropped and rejects as canceled
 * without calling `start`. In-flight aborts are handled by the caller's AbortSignal
 * (e.g. axios config); the active slot is always released in `finally`.
 *
 * @param {() => Promise<T>} start
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<T>}
 * @template T
 */
export function enqueueReportRun(start, { signal } = {}) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(canceledError());
      return;
    }

    /** @type {Job} */
    const job = {
      signal,
      start,
      resolve,
      reject,
      settled: false,
    };

    job.onAbort = () => {
      const idx = waiting.indexOf(job);
      if (idx === -1) return; // already running or settled — axios/signal handles in-flight
      waiting.splice(idx, 1);
      settle(job, reject, canceledError());
    };

    if (signal) {
      signal.addEventListener('abort', job.onAbort);
    }

    waiting.push(job);
    pump();
  });
}

export const REPORT_RUN_CONCURRENCY = MAX_CONCURRENT;
